/* eslint-disable */
// @ts-nocheck

import { Check } from 'lucide-react'
import React from 'react'

import { cn } from '@/lib/utils'

import { Button } from './button'

interface StepProps {
  children: React.ReactNode
  optional?: boolean
  isCompleted?: boolean
  isActive?: boolean
  isLastVisible?: boolean
  isHidden?: boolean
  index?: number
  displayIndex?: number
  onStepClick?: (index: number) => void
  className?: string
  isLast?: boolean
}

/**
 * Represents a single step within the Stepper component.
 * It visually indicates whether the step is completed, active, or pending,
 * and connects to the next step if applicable. Can be hidden via animation.
 */
export const Step: React.FC<StepProps> = ({
  children,
  isCompleted,
  isActive,
  isLastVisible,
  isHidden,
  index,
  displayIndex,
  onStepClick,
  className,
  isLast,
}) => {
  const baseClasses =
    'flex items-center transition-all duration-300 ease-in-out'
  const visibilityClasses = isHidden
    ? 'w-0 opacity-0 overflow-hidden pointer-events-none'
    : 'flex-1 max-w-xs opacity-100'

  const lastElementClasses = isLast && 'flex-grow-0'

  const canClick =
    isCompleted && !isActive && index !== undefined && onStepClick

  return (
    <div
      aria-hidden={isHidden}
      className={cn(
        baseClasses,
        visibilityClasses,
        lastElementClasses,
        className
      )}
    >
      {!isHidden && (
        <>
          <div className="relative flex flex-col items-center">
            {/* Circle */}
            <Button
              className={cn(
                'z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 p-0 transition-all duration-300',
                isCompleted
                  ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/10'
                  : '',
                isActive
                  ? 'border-primary hover:cursor-default hover:bg-transparent'
                  : 'border-muted',
                !isCompleted && !isActive
                  ? 'bg-muted/30 text-muted-foreground hover:cursor-default hover:bg-muted/30 hover:text-muted-foreground'
                  : '',
                canClick ? 'cursor-pointer' : ''
              )}
              onClick={() => {
                if (canClick) {
                  onStepClick(index)
                }
              }}
              tabIndex={isHidden ? -1 : 0}
              variant="outline"
            >
              {isCompleted ? (
                <Check className="h-5 w-5" />
              ) : (
                <span
                  className={cn(
                    isActive ? 'font-semibold text-primary' : 'text-sm'
                  )}
                >
                  {displayIndex !== undefined ? displayIndex + 1 : ''}
                </span>
              )}
            </Button>
            {/* Label */}
            <div
              className={cn(
                'mt-2 whitespace-nowrap px-1 text-center text-xs leading-tight',
                canClick ? 'cursor-pointer' : ''
              )}
              onClick={() => {
                if (canClick) {
                  onStepClick(index)
                }
              }}
            >
              <span
                className={cn(
                  isActive
                    ? 'font-medium text-primary'
                    : 'text-muted-foreground',
                  isCompleted ? 'font-medium text-foreground' : ''
                )}
              >
                {children}
              </span>
            </div>
          </div>
          {!isLastVisible && !isLast && (
            <div className="relative mx-2 mb-6 h-0.5 flex-1 bg-border">
              <div
                className={cn(
                  'absolute left-0 top-0 h-full bg-primary transition-[width] duration-300 ease-in-out',
                  isCompleted ? 'w-full' : 'w-0'
                )}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

interface StepperProps {
  children: React.ReactElement<StepProps>[] | React.ReactElement<StepProps>
  activeStep: number
  className?: string
  onStepClick?: (index: number) => void
}

/**
 * A component that displays progress through a series of steps.
 * It arranges Step components horizontally, manages their active/completed states,
 * handles hiding steps, and calculates visible step numbers dynamically.
 */
export const Stepper: React.FC<StepperProps> = ({
  children,
  activeStep,
  className,
  onStepClick,
}) => {
  const steps = React.Children.toArray(
    children
  ) as React.ReactElement<StepProps>[]

  let visibleStepIndex = 0
  const visibleSteps = steps.filter((step) => !step.props.isHidden)
  const lastVisibleStepIndex =
    visibleSteps.length > 0
      ? steps.indexOf(visibleSteps[visibleSteps.length - 1])
      : -1

  return (
    <div
      className={cn(
        'flex w-full items-start justify-between transition-all duration-300 ease-in-out',
        className
      )}
    >
      {steps.map((step, index) => {
        const isHidden = step.props.isHidden ?? false
        const isCompleted = index < activeStep
        const isActive = index === activeStep
        const isLastVisible = index === lastVisibleStepIndex
        const isLast = index === steps.length - 1

        let currentDisplayIndex: number | undefined = undefined
        if (!isHidden) {
          currentDisplayIndex = visibleStepIndex
          visibleStepIndex++
        }

        return React.cloneElement(step, {
          key: `step-${index}`,
          index: index,
          displayIndex: currentDisplayIndex,
          isActive: isActive,
          isCompleted: isCompleted,
          isLastVisible: isLastVisible,
          isLast: isLast,
          isHidden: isHidden,
          onStepClick: onStepClick,
        })
      })}
    </div>
  )
}
